import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/cn";
import { useToast } from "@/lib/toast";

interface WhatsAppStatus {
  configured: boolean;
  sessionName: string | null;
  status: string;
  phone: string | null;
  pushName: string | null;
  detail: string | null;
}

interface SecretsStatus {
  database_url: boolean;
  admin_token: boolean;
  openrouter_api_key: boolean;
  waha_api_key: boolean;
  waha_base_url: boolean;
  waha_default_session: boolean;
  meta_verify_token: boolean;
  meta_app_secret: boolean;
  meta_page_access_token: boolean;
  resend_api_key: boolean;
  resend_from_email: boolean;
  telegram_bot_token: boolean;
}

type ChannelKey = "in_app" | "email" | "telegram" | "whatsapp";

export function ConnectionsPage() {
  const form = useBusinessSettingsForm();
  const test = useChannelTest();
  const { data: secrets } = useQuery({
    queryKey: ["system", "secrets-status"],
    queryFn: () => api.get<SecretsStatus>("/api/system/secrets-status"),
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-5 pb-24">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Connections</h1>
        <p className="text-sm text-slate-500">
          Where prospects talk to the AI, and where the AI pings you back.
        </p>
      </header>

      <Section title="Inbound - prospects → bot" hint="Channels the AI reads and replies on.">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <WhatsAppInboundCard />
          <FacebookPageCard />
          <InstagramCard />
        </div>
      </Section>

      <Section title="Notifications - bot → you" hint="Where you get pinged on hot leads, takeovers and visits.">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <WhatsAppNotifCard form={form} test={test} />
          <TelegramNotifCard form={form} test={test} secrets={secrets} />
          <EmailNotifCard form={form} test={test} secrets={secrets} />
        </div>
      </Section>

      <FaqSection />

      {form.dirty && (
        <div className="fixed bottom-4 right-6 z-20 flex items-center gap-3 rounded-lg border border-slate-300 bg-brand-surface px-4 py-2 shadow-md">
          <span className="text-xs text-slate-600">Unsaved changes</span>
          <Button size="sm" variant="ghost" onClick={() => form.discard()}>Discard</Button>
          <Button size="sm" onClick={() => form.saveMut.mutate()} disabled={form.saveMut.isPending}>
            {form.saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared hooks
// ---------------------------------------------------------------------------

function useBusinessSettingsForm() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({
    queryKey: ["settings", "/api/settings/business"],
    queryFn: () => api.get<Record<string, unknown>>("/api/settings/business"),
  });
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const saveMut = useMutation({
    mutationFn: () => api.put("/api/settings/business", draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings", "/api/settings/business"] });
      const n = Object.keys(draft).length;
      setDraft({});
      toast.success("Connections saved", `${n} field${n > 1 ? "s" : ""} updated`);
    },
    onError: (e) => toast.error("Save failed", e instanceof Error ? e.message : String(e)),
  });
  const get = (k: string) => (draft[k] !== undefined ? draft[k] : data?.[k]);
  const set = (k: string, v: unknown) => setDraft({ ...draft, [k]: v });
  const isBool = (k: string) => {
    const v = get(k);
    return v === 1 || v === true;
  };
  const discard = () => setDraft({});
  const dirty = Object.keys(draft).length > 0;
  return { data, draft, saveMut, get, set, isBool, dirty, discard };
}

type FormHook = ReturnType<typeof useBusinessSettingsForm>;

function useChannelTest() {
  const toast = useToast();
  const [busy, setBusy] = useState<ChannelKey | null>(null);
  const [results, setResults] = useState<Record<string, { ok: boolean; detail: string }>>({});

  async function send(channel: ChannelKey) {
    setBusy(channel);
    try {
      const res = await api.post<{ ok: boolean; detail: string }>("/api/notifications/test", { channel });
      setResults((r) => ({ ...r, [channel]: res }));
      if (res.ok) toast.success(`Test sent via ${channel}`, res.detail);
      else toast.error(`${channel} test failed`, res.detail);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setResults((r) => ({ ...r, [channel]: { ok: false, detail: message } }));
      toast.error(`${channel} test failed`, message);
    } finally {
      setBusy(null);
    }
  }

  return { busy, results, send };
}

type TestHook = ReturnType<typeof useChannelTest>;

// ---------------------------------------------------------------------------
// Inbound cards
// ---------------------------------------------------------------------------

function WhatsAppInboundCard() {
  const { data: status } = useQuery({
    queryKey: ["channels", "whatsapp", "status"],
    queryFn: () => api.get<WhatsAppStatus>("/api/channels/whatsapp/status"),
    refetchInterval: 15_000,
  });

  return (
    <CompactCard
      icon={<BrandLogo name="whatsapp" />}
      name="WhatsApp"
      badge={<StatusBadge status={status?.status} />}
    >
      {!status ? (
        <Line muted>Loading…</Line>
      ) : !status.configured ? (
        <Line muted>WAHA not configured. See FAQ below.</Line>
      ) : (
        <>
          <Line>{status.phone ?? "-"}</Line>
          <Line muted>Session · {status.sessionName ?? "(none)"}</Line>
          {status.detail && <Line tone="err">{status.detail}</Line>}
        </>
      )}
    </CompactCard>
  );
}

function FacebookPageCard() {
  return (
    <CompactCard
      icon={<BrandLogo name="facebook" />}
      name="Facebook Page"
      badge={<Badge variant="outline">Not connected</Badge>}
    >
      <Line muted>Messenger inbound via Meta App. See FAQ.</Line>
    </CompactCard>
  );
}

function InstagramCard() {
  return (
    <CompactCard
      icon={<BrandLogo name="instagram" />}
      name="Instagram"
      badge={<Badge variant="outline">Not connected</Badge>}
    >
      <Line muted>Requires a linked Facebook Page. See FAQ.</Line>
    </CompactCard>
  );
}

// ---------------------------------------------------------------------------
// Notification cards
// ---------------------------------------------------------------------------

function WhatsAppNotifCard({ form, test }: { form: FormHook; test: TestHook }) {
  const enabled = form.isBool("notifications_whatsapp_enabled");
  const phone = String(form.get("notifications_whatsapp_phone") ?? "");
  const active = enabled && phone.trim().length > 0;

  return (
    <CompactCard
      icon={<BrandLogo name="whatsapp" />}
      name="WhatsApp (personal)"
      badge={<ActiveBadge active={active} />}
    >
      <EnabledToggle
        label="Enabled"
        checked={enabled}
        onChange={(v) => form.set("notifications_whatsapp_enabled", v)}
      />
      <Input
        placeholder="+33612345678"
        value={phone}
        onChange={(e) => form.set("notifications_whatsapp_phone", e.target.value)}
      />
      <TestRow channel="whatsapp" test={test} />
    </CompactCard>
  );
}

function TelegramNotifCard({
  form, test, secrets,
}: { form: FormHook; test: TestHook; secrets: SecretsStatus | undefined }) {
  const enabled = form.isBool("notifications_telegram_enabled");
  const chatId = String(form.get("notifications_telegram_chat_id") ?? "");
  const active = enabled && chatId.trim().length > 0;
  const tokenMissing = secrets !== undefined && !secrets.telegram_bot_token;

  return (
    <CompactCard
      icon={<BrandLogo name="telegram" />}
      name="Telegram"
      badge={<ActiveBadge active={active} />}
    >
      {tokenMissing && (
        <SecretWarning label="TELEGRAM_BOT_TOKEN" anchor="telegram-setup" />
      )}
      <EnabledToggle
        label="Enabled"
        checked={enabled}
        onChange={(v) => form.set("notifications_telegram_enabled", v)}
      />
      <Input
        placeholder="Chat ID (numeric)"
        value={chatId}
        onChange={(e) => form.set("notifications_telegram_chat_id", e.target.value)}
      />
      <TestRow channel="telegram" test={test} />
    </CompactCard>
  );
}

function EmailNotifCard({
  form, test, secrets,
}: { form: FormHook; test: TestHook; secrets: SecretsStatus | undefined }) {
  const enabled = form.isBool("notifications_email_enabled");
  const email = String(form.get("notifications_email") ?? "");
  const active = enabled && email.trim().length > 0;
  const keyMissing = secrets !== undefined && !secrets.resend_api_key;

  return (
    <CompactCard
      icon={<BrandLogo name="email" />}
      name="Email"
      badge={<ActiveBadge active={active} />}
    >
      {keyMissing && <SecretWarning label="RESEND_API_KEY" anchor="email-setup" />}
      <EnabledToggle
        label="Enabled"
        checked={enabled}
        onChange={(v) => form.set("notifications_email_enabled", v)}
      />
      <Input
        type="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => form.set("notifications_email", e.target.value)}
      />
      <TestRow channel="email" test={test} />
    </CompactCard>
  );
}

function SecretWarning({ label, anchor }: { label: string; anchor: string }) {
  return (
    <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1 flex items-start gap-1.5">
      <span aria-hidden>⚠️</span>
      <span>
        <code className="font-mono">{label}</code> not set.{" "}
        <a href={`#${anchor}`} className="underline">See setup ↓</a>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FAQ - all setup + troubleshooting in one place
// ---------------------------------------------------------------------------

function FaqSection() {
  return (
    <section className="pt-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
        Setup & troubleshooting
      </h2>
      <Card>
        <CardContent className="p-0 divide-y divide-slate-200">
          <FaqItem question="WhatsApp shows 'Not configured' or 'Unreachable'">
            <p>Three secrets must be set as Cloudflare Worker secrets:</p>
            <ul className="ml-5 list-disc space-y-0.5">
              <li><code>WAHA_API_KEY</code></li>
              <li><code>WAHA_BASE_URL</code> - your partner-hosted WAHA instance</li>
              <li><code>WAHA_DEFAULT_SESSION</code> - usually <code>default</code></li>
            </ul>
            <p>The WAHA instance must point its webhook at <code>/webhooks/waha</code> on this worker. To swap the business number, contact your WAHA partner.</p>
          </FaqItem>

          <FaqItem question="WhatsApp shows 'Awaiting QR scan'">
            <p>Open your WAHA dashboard, scan the QR code with the business phone, then come back here - the status flips to <strong>Connected</strong> within ~15 s.</p>
          </FaqItem>

          <FaqItem question="Connect a Facebook Page (Messenger inbound)">
            <ol className="ml-5 list-decimal space-y-0.5">
              <li>Create an app at <a className="underline" href="https://developers.facebook.com" target="_blank" rel="noreferrer">developers.facebook.com</a>.</li>
              <li>Request perms: <code>pages_messaging</code>, <code>pages_show_list</code>, <code>pages_read_engagement</code>.</li>
              <li>Submit for App Review (~2-5 days).</li>
              <li>Set <code>META_APP_SECRET</code>, <code>META_VERIFY_TOKEN</code>, <code>META_PAGE_ACCESS_TOKEN</code> as worker secrets.</li>
              <li>Subscribe the page webhook to <code>/webhooks/meta</code> on this worker.</li>
            </ol>
          </FaqItem>

          <FaqItem question="Connect Instagram DMs">
            <ol className="ml-5 list-decimal space-y-0.5">
              <li>Connect a Facebook Page first (above).</li>
              <li>The IG account must be <strong>Business</strong> or <strong>Creator</strong> - personal accounts cannot connect.</li>
              <li>Link the IG account to the Page from the IG mobile app → Settings → Account → Linked accounts.</li>
            </ol>
          </FaqItem>

          <FaqItem anchor="telegram-setup" question="Set up Telegram notifications (5 min)">
            <ol className="ml-5 list-decimal space-y-0.5">
              <li>Talk to <a className="underline" href="https://t.me/BotFather" target="_blank" rel="noreferrer">@BotFather</a>, send <code>/newbot</code>, copy the token.</li>
              <li>Set the token: <code>wrangler secret put TELEGRAM_BOT_TOKEN</code>.</li>
              <li>Open your bot in Telegram and send it <code>/start</code>.</li>
              <li>Message <a className="underline" href="https://t.me/userinfobot" target="_blank" rel="noreferrer">@userinfobot</a> to get your numeric chat ID - paste it above.</li>
              <li>Enable, save, send a test.</li>
            </ol>
          </FaqItem>

          <FaqItem anchor="email-setup" question="Set up Email notifications (via Resend)">
            <ol className="ml-5 list-decimal space-y-0.5">
              <li>Sign up at <a className="underline" href="https://resend.com" target="_blank" rel="noreferrer">resend.com</a> (free tier: 100/day).</li>
              <li>Create an API key: <code>wrangler secret put RESEND_API_KEY</code>.</li>
              <li>For production, verify a sending domain and set <code>RESEND_FROM_EMAIL</code> (default <code>onboarding@resend.dev</code>).</li>
              <li>Enter the destination email above, enable, save, send a test.</li>
            </ol>
          </FaqItem>

          <FaqItem question="The test button failed - what to check">
            <ul className="ml-5 list-disc space-y-0.5">
              <li>Did you click <strong>Save</strong> after changing the destination? Tests use the saved value, not the draft.</li>
              <li>For WhatsApp personal: use a phone different from the business number - same number = self-loop.</li>
              <li>For Telegram: did you send <code>/start</code> to your bot? Bots can't message users who haven't initiated contact.</li>
              <li>For Email: in dev, only the address tied to the Resend account can receive - verify a domain to send anywhere.</li>
            </ul>
          </FaqItem>

          <FaqItem question="Can I notify a personal Facebook or Instagram account?">
            <p>No. Meta's 24-hour messaging window blocks outbound notifications outside an active conversation. Use WhatsApp, Telegram or Email for personal alerts.</p>
          </FaqItem>
        </CardContent>
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</h2>
        {hint && <p className="text-xs text-slate-400">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function CompactCard({
  icon, name, badge, children,
}: {
  icon: React.ReactNode;
  name: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <CardTitle className="flex items-center justify-between gap-2 text-sm">
          <span className="flex items-center gap-2">
            {icon}
            <span>{name}</span>
          </span>
          {badge}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 px-4 pb-4 pt-0 text-sm">
        {children}
      </CardContent>
    </Card>
  );
}

type BrandName = "whatsapp" | "facebook" | "instagram" | "telegram" | "email";

const BRAND_BG: Record<BrandName, string> = {
  whatsapp: "#25D366",
  facebook: "#1877F2",
  instagram: "linear-gradient(135deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)",
  telegram: "#229ED9",
  email: "#475569",
};

function BrandLogo({ name }: { name: BrandName }) {
  return (
    <span
      aria-hidden
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-white"
      style={{ background: BRAND_BG[name] }}
    >
      <BrandGlyph name={name} />
    </span>
  );
}

function BrandGlyph({ name }: { name: BrandName }) {
  const common = { width: 14, height: 14, viewBox: "0 0 24 24", fill: "currentColor" } as const;
  switch (name) {
    case "whatsapp":
      return (
        <svg {...common}>
          <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.01Zm-7.01 15.24a8.22 8.22 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.26 8.26 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.41 5.83c.01 4.54-3.69 8.23-8.23 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18s-.22-.16-.47-.28Z" />
        </svg>
      );
    case "facebook":
      return (
        <svg {...common}>
          <path d="M24 12.07C24 5.44 18.63.07 12 .07S0 5.44 0 12.07c0 6 4.39 10.96 10.13 11.85v-8.39H7.08v-3.46h3.05V9.43c0-3.01 1.79-4.67 4.53-4.67 1.31 0 2.69.24 2.69.24v2.95H15.83c-1.49 0-1.96.93-1.96 1.88v2.24h3.33l-.53 3.46h-2.8v8.39C19.61 23.03 24 18.07 24 12.07Z" />
        </svg>
      );
    case "instagram":
      return (
        <svg {...common}>
          <path d="M12 2.16c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.15 3.22-1.66 4.77-4.92 4.92-1.27.06-1.64.07-4.85.07s-3.58-.01-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92-.06-1.27-.07-1.64-.07-4.85s.01-3.58.07-4.85c.15-3.23 1.66-4.77 4.92-4.92 1.27-.06 1.65-.07 4.85-.07Zm0-2.16C8.74 0 8.33.01 7.05.07 2.7.27.27 2.69.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.2 4.36 2.62 6.78 6.98 6.98C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c4.35-.2 6.78-2.62 6.98-6.98.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95C23.73 2.69 21.31.27 16.95.07 15.67.01 15.26 0 12 0Zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32Zm0 10.16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.41-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88Z" />
        </svg>
      );
    case "telegram":
      return (
        <svg {...common}>
          <path d="M11.94 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 11.94 0Zm4.97 7.22c.1 0 .32.03.46.14.1.08.16.2.17.33.02.09.04.3.02.47-.18 1.9-.96 6.5-1.36 8.63-.17.9-.5 1.2-.82 1.23-.7.07-1.23-.46-1.9-.9-1.06-.69-1.65-1.12-2.68-1.8-1.19-.78-.42-1.21.26-1.91.18-.18 3.25-2.98 3.31-3.23.01-.03.01-.15-.06-.21-.07-.06-.17-.04-.25-.02-.1.02-1.79 1.14-5.06 3.34-.48.33-.91.49-1.3.48-.43-.01-1.25-.24-1.87-.44-.75-.24-1.35-.37-1.3-.79.03-.22.33-.44.9-.66 3.5-1.52 5.83-2.53 7-3.01 3.33-1.39 4.02-1.63 4.48-1.64Z" />
        </svg>
      );
    case "email":
      return (
        <svg {...common}>
          <path d="M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Zm9 8.36 8.4-5.6L20 6.6 12 12 4 6.6l-.4 1.16L12 13.36Z" />
        </svg>
      );
  }
}

function Line({
  children, muted, tone,
}: { children: React.ReactNode; muted?: boolean; tone?: "err" }) {
  return (
    <div className={cn(
      "text-xs",
      muted ? "text-slate-500" : "text-slate-700",
      tone === "err" && "text-red-700",
    )}>
      {children}
    </div>
  );
}

function StatusBadge({ status }: { status: string | undefined }) {
  if (!status) return <Badge variant="outline">…</Badge>;
  const map: Record<string, { label: string; tone: "ok" | "warn" | "err" | "muted" }> = {
    WORKING: { label: "Connected", tone: "ok" },
    SCAN_QR_CODE: { label: "Scan QR", tone: "warn" },
    STARTING: { label: "Starting", tone: "warn" },
    STOPPED: { label: "Stopped", tone: "err" },
    FAILED: { label: "Failed", tone: "err" },
    not_found: { label: "No session", tone: "err" },
    not_configured: { label: "Not configured", tone: "muted" },
    unreachable: { label: "Unreachable", tone: "err" },
    error: { label: "Error", tone: "err" },
  };
  const entry = map[status] ?? { label: status, tone: "muted" as const };
  const cls = {
    ok: "bg-green-100 text-green-800 border-green-200",
    warn: "bg-amber-100 text-amber-800 border-amber-200",
    err: "bg-red-100 text-red-800 border-red-200",
    muted: "bg-slate-100 text-slate-700 border-slate-200",
  }[entry.tone];
  return (
    <span className={cn("text-[10px] font-medium px-2 py-0.5 rounded border", cls)}>
      {entry.label}
    </span>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="text-[10px] font-medium px-2 py-0.5 rounded border bg-green-100 text-green-800 border-green-200">
      Active
    </span>
  ) : (
    <span className="text-[10px] font-medium px-2 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200">
      Off
    </span>
  );
}

function EnabledToggle({
  label, checked, onChange,
}: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5"
      />
      <span className="text-xs text-slate-700">{label}</span>
    </label>
  );
}

function TestRow({ channel, test }: { channel: ChannelKey; test: TestHook }) {
  const r = test.results[channel];
  const testing = test.busy === channel;
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => test.send(channel)}
        disabled={testing}
        className="h-7 px-2 text-[11px]"
      >
        {testing ? "Sending…" : "Send test"}
      </Button>
      {r && (
        <span className={cn(
          "text-[10px] truncate",
          r.ok ? "text-green-700" : "text-red-700",
        )} title={r.detail}>
          {r.ok ? "✓ sent" : `✗ ${r.detail}`}
        </span>
      )}
    </div>
  );
}

function FaqItem({
  question, anchor, children,
}: {
  question: string;
  /** When set, the <details> opens automatically if the URL hash matches. */
  anchor?: string;
  children: React.ReactNode;
}) {
  const open = typeof window !== "undefined" && anchor !== undefined
    && window.location.hash === `#${anchor}`;
  return (
    <details id={anchor} className="group" open={open}>
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 list-none flex items-center justify-between">
        <span>{question}</span>
        <span className="text-slate-400 text-xs group-open:rotate-90 transition-transform">▶</span>
      </summary>
      <div className="px-4 pb-3 pt-1 text-xs text-slate-600 space-y-2">
        {children}
      </div>
    </details>
  );
}
